export const AGENT_CONFIG_PATH = "agents/paperclip/paperclip.yaml";
export const PEER_REVIEW_AGENT_CONFIG_PATH = "agents/peer_review/peer_review.yaml";

/** Session list scope — maps to the backend `session_scope` query param. */
export type SessionScope = "paperclip" | "peer_review";

// ---------------------------------------------------------------------------
// API types
// ---------------------------------------------------------------------------

export interface Session {
  session_id: string;
  title: string | null;
  agent_config_path: string | null;
  created_at: string;
  updated_at: string;
  message_count: number;
}

export interface Message {
  id: string;
  role: "user" | "assistant" | "tool";
  content: string | null;
  created_at: string;
  tool_calls: ToolCallData[];
  tool_call_id: string | null;
  isStreaming?: boolean;
  agent_config_path?: string | null;
}

export interface ToolCallData {
  id: string;
  function: { name: string; arguments: string };
  /** On a call made by a subagent: the id of the Agent call that started it. */
  parent_tool_call_id?: string;
}

export interface ToolCallDelta {
  index: number;
  id?: string;
  function?: { name?: string; arguments?: string };
}

export interface AttachmentFile {
  filename: string;
  content: string; // base64 data URL
  mime_type: string;
  size: number;
}

export interface ChatRequest {
  message: string;
  sessionId?: string;
  agent_config_path?: string;
  executeTools?: boolean;
  attachments?: AttachmentFile[];
}

// ---------------------------------------------------------------------------
// SSE stream chunks (discriminated union)
// ---------------------------------------------------------------------------

export type StreamChunk =
  | { type: "session_id"; session_id: string }
  | { type: "content"; data: string }
  | { type: "tool_calls"; data: ToolCallData[] }
  | { type: "tool_calls_delta"; data: ToolCallDelta[] }
  | { type: "tool_execution_started"; tool_count: number; round?: number }
  | { type: "tool_execution_result"; results: ToolResult[] }
  | { type: "tool_progress"; command: string; completed: number; failed: number; total: number; elapsed_s: number }
  | { type: "user_message_saved"; message_id: string }
  | { type: "done" }
  | { type: "error"; error: string };

export interface ToolResult {
  tool_call_id: string;
  tool_name: string;
  content: string;
  success?: boolean;
  duration_ms?: number;
}

// ---------------------------------------------------------------------------
// Display model
// ---------------------------------------------------------------------------

export type TraceItem =
  | { type: "text"; content: string }
  | { type: "tool_call"; toolCall: TraceToolCall };

export interface TraceTurn {
  id: string;
  type: "user" | "assistant";
  content?: string;
  items?: TraceItem[];
  finalResponse?: string;
  isStreaming?: boolean;
  fadeHold?: boolean;
  attachments?: AttachmentFile[];
}

export interface TraceToolCall {
  id: string;
  name: string;
  /** "stopped": the run ended, for example with Stop, before the call returned. */
  status: "pending" | "running" | "complete" | "error" | "stopped";
  input?: Record<string, unknown>;
  output?: string;
  duration?: string;
  parentId?: string;
  /** The calls of the subagent that this Agent call started. */
  children?: TraceToolCall[];
}

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

/** Scope-partitioned key so Paperclip and Peer Review lists don't collide. */
export const sessionsQueryKey = (scope: SessionScope = "paperclip") =>
  ["chat-sessions", { sessionScope: scope }] as const;

export const queryKeys = {
  sessions: sessionsQueryKey("paperclip"),
  messages: (sessionId: string) => ["chat-messages", sessionId] as const,
  sessionStatus: (sessionId: string) =>
    ["chat-session-status", sessionId] as const,
};
